import { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CsvDownloadHandler,
  GetCalendarData,
  GetSessionDetail,
  ProcessRawCSVFile,
  GetJobStatus,
} from "../../../utils/apiCalls/patients";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { downloadBlobUrl, downloadFilesAsZip } from "../../../utils/downloadFile";
import { useToast } from "../../../utils/ToastContext";
dayjs.extend(utc);

const formatTimestamp = (dateStr) => dayjs(dateStr).format("YYYYMMDDHHmmss");

export const formatDate = (dateString) =>
  dayjs(dateString).format("YYYY-MM-DD HH:mm:ss");

export const formatDateOnly = (dateString) =>
  dayjs(dateString).format("YYYY-MM-DD");

export const formatSecondsToHMS = (totalSeconds) => {
  if (!totalSeconds && totalSeconds !== 0) return "00:00:00";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return [
    hours.toString().padStart(2, "0"),
    minutes.toString().padStart(2, "0"),
    seconds.toString().padStart(2, "0"),
  ].join(":");
};

export default function useAdminPatientsSession() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [referenceModal, setReferenceModal] = useState(false);
  const [referenceData, setReferenceData] = useState([]);
  const [, setDeleteConfirmation] = useState(false);
  const [, setDeleteSessionId] = useState(null);
  const [downloadOptionsOpen, setDownloadOptionsOpen] = useState(false);
  const [downloadOptionsRow, setDownloadOptionsRow] = useState(null);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [isDownloadingProcessedCsv, setIsDownloadingProcessedCsv] = useState(false);
  // null while no zip is actively being built (so the popup falls back to an
  // indeterminate spinner instead of a misleading "0%"); a number once bytes
  // are actually streaming in for the zip step.
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [downloadPopupOpen, setDownloadPopupOpen] = useState(false);
  const [downloadPopupTitle, setDownloadPopupTitle] = useState("");
  const [downloadPopupLoading, setDownloadPopupLoading] = useState(false);
  const activeSessionRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const isPopupClosedRef = useRef(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (location?.state?.selectedDateReturn) {
      return dayjs(location.state.selectedDateReturn);
    }
    const stored = sessionStorage.getItem("returnSelectedDate");
    if (stored) {
      sessionStorage.removeItem("returnSelectedDate");
      return dayjs(stored);
    }
    return null;
  });

  const rowData = sessionStorage.getItem("rowData");
  const [highlightedDates, setHighlightedDates] = useState([]);

  // Opened directly or after a refresh with no patient context in
  // sessionStorage: nothing to fetch, redirect to the dashboard instead of
  // hitting the session APIs with a missing patient id.
  useEffect(() => {
    if (!rowData) {
      navigate("/admin-dashboard", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (dates) => {
    const date = dates[0];
    if (!date) return;
    setSelectedDate(dayjs(date));
  };

  const fetchSessionDetails = async (pageIndex, pageSize, selectedDateParam, isStale = () => false) => {
    if (!selectedDateParam) return; // safety guard

    setIsLoading(true);
    setFetchError("");
    const formattedDate = dayjs(selectedDateParam).format("YYYY-MM-DD");
    const payload = {
      pageIndex: pageIndex,
      pageSize: 50,
      searchString: "",
      selectedDate: formattedDate,
    };

    try {
      const response = await GetSessionDetail(rowData, payload);
      if (isStale()) return;
      if (response.statusCode === 200) {
        const sessions = response.data.map((note) => {
          const selectDate = new Date(note.createdDate);
          return { ...note, selectDate };
        });
        setRows(sessions);
        setTotalRows(response.totalRecords);
      } else if (response.statusCode === 204) {
        setRows([]);
        showToast("Data Not Available", "info");
      } else {
        console.error("Error fetching session details:", response.message);
        setRows([]);
        setFetchError(response.message || "Failed to load sessions");
        showToast("Internal Server Error", "error");
      }
    } catch (error) {
      if (!isStale()) {
        console.error("Error fetching session details:", error?.message);
        setRows([]);
        setFetchError(error?.message || "Failed to load sessions");
        showToast("Internal Server Error", "error");
      }
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  };

  // Use selectedDate to fetch table data
  useEffect(() => {
    if (!selectedDate) return; // don't call API until we know the correct date
    let ignore = false;
    fetchSessionDetails(page, rowsPerPage, selectedDate, () => ignore);
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, selectedDate]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (referenceId) => {
    setDeleteSessionId(referenceId);
    setDeleteConfirmation(true);
  };

  const handleView = (row) => {
    const sessionRow = activeSessionRef.current;
    navigate("/superadmin-graph", {
      state: {
        referenceData: row,
        sessionId: row.id,
        referenceId: row.referenceId,
        isJsonData: sessionRow?.isJsonData ?? null,
        selectedDate: selectedDate ? selectedDate.format("YYYY-MM-DD") : null,
      },
    });
  };

  const handleTable = () => {
    navigate(-1);
  };

  const handleMainTableView = (row) => {
    // Go directly to graph page, do not show modal
    navigate("/superadmin-graph", {
      state: {
        referenceData: row,
        sessionId: row.id,
        referenceId: row.referenceId,
        isJsonData: row.isJsonData ?? null,
        selectedDate: row.startTime
          ? dayjs(row.startTime).format("YYYY-MM-DD")
          : null,
      },
    });
  };

  const handleDownload = async (row) => {
    try {
      setReferenceModal(false);
      let response;
      try {
        response = await CsvDownloadHandler(row.id);
      } catch (fetchError) {
        response = fetchError;
      }
      setIsLoading(false);
      if (response.statusCode === 200) {
        setReferenceData(response.data);
      } else {
        setReferenceModal(false);
        showToast(response.message, "error");
        throw new Error("Internal Server Error");
      }

      const s3Url = `${row.baseUrl}${row.csvUrl}`;
      const startDate = formatDate(row?.startDate).replace(/[:\s]/g, "-");
      const endDate = formatDate(row?.endDate).replace(/[:\s]/g, "-");
      await downloadBlobUrl(s3Url, `${startDate}_TO_${endDate}.csv`);
      showToast("File downloaded successfully", "success");
    } catch (error) {
      console.error("Error handling the download process:", error);
      // The statusCode !== 200 branch above already opened the modal with a
      // server message; only show a generic one for unexpected failures
      // (e.g. the CSV file fetch itself failing).
      if (error?.message !== "Internal Server Error") {
        showToast("Error downloading CSV file", "error");
      }
    }
  };

  const fetchCalendarData = async () => {
    const hadInitialDate = Boolean(
      selectedDate || location?.state?.selectedDateReturn,
    );
    try {
      const response = await GetCalendarData(rowData);
      if (response.statusCode === 200) {
        const dates = response?.data?.map((item) =>
          dayjs(item.date).format("YYYY-MM-DD"),
        );
        setHighlightedDates(dates);

        // If no selectedDate came from navigation, pick the latest available date <= today
        if (!hadInitialDate && dates.length) {
          const todayStr = dayjs().format("YYYY-MM-DD");
          const validDates = dates.filter((d) => d <= todayStr);
          if (validDates.length) {
            const sorted = validDates.toSorted(); // ascending
            const lastAvailable = sorted[sorted.length - 1]; // latest date with data
            setSelectedDate(dayjs(lastAvailable));
            return;
          }
        }
      } else {
        setHighlightedDates([]);
      }
    } catch (error) {
      setHighlightedDates([]);
    }
    // No date resolved (no calendar data / no valid date <= today), so the
    // session fetch effect will never run — stop the table's loading state.
    if (!hadInitialDate) setIsLoading(false);
  };

  useEffect(() => {
    fetchCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    if (!selectedDate) return;
    fetchSessionDetails(page, rowsPerPage, selectedDate);
  };

  // Handler for main table download icon
  const handleMainTableDownload = (row) => {
    setDownloadOptionsRow(row);
    setDownloadOptionsOpen(true);
  };

  // Handler for downloading CSV URL
  const handleDownloadCSVUrl = () => {
    // Guard against a second click landing while a download is in flight.
    if (isDownloadingCsv || isDownloadingProcessedCsv) return;

    if (!downloadOptionsRow || !downloadOptionsRow.csvUrl) {
      showToast("CSV file not available", "error");
      setDownloadOptionsOpen(false);
      return;
    }

    const Name = sessionStorage.getItem("patientName");
    const DeviceId = sessionStorage.getItem("deviceId");
    const formattedName = Name?.replace(/\s+/g, "_") || "patient";
    const uuidShort = (downloadOptionsRow.referenceId || "unknown").split(
      "-",
    )[0];
    const startFormatted = formatTimestamp(
      downloadOptionsRow?.startDate || downloadOptionsRow?.startTime || "",
    );
    const endFormatted = formatTimestamp(
      downloadOptionsRow?.endDate || downloadOptionsRow?.endTime || "",
    );
    const deviceId = DeviceId?.replace(/We-Be\s*/i, "") || "device";
    const filename = `${formattedName}_${startFormatted}_${endFormatted}_${uuidShort}_${deviceId}.csv`;

    setIsDownloadingCsv(true);

    downloadBlobUrl(downloadOptionsRow.csvUrl, filename)
      .then(async () => {
        showToast("CSV file downloaded successfully", "success");
        // Call CsvDownloadHandler to track download count
        try {
          await CsvDownloadHandler(downloadOptionsRow.id);
          // Refresh GetSessionDetail to get updated counts
          const refreshPayload = {
            pageIndex: 0,
            pageSize: 50,
            searchString: "",
            selectedDate: selectedDate ? selectedDate.format("YYYY-MM-DD") : "",
          };
          const refreshResponse = await GetSessionDetail(rowData, refreshPayload);
          if (refreshResponse.statusCode === 200) {
            setRows(refreshResponse.data);
          }
        } catch (error) {
          console.error("Error tracking CSV download:", error?.message);
        }
      })
      .catch((err) => {
        console.error("CSV download error:", err);
        showToast("Error downloading CSV file", "error");
      })
      .finally(() => {
        setIsDownloadingCsv(false);
        setDownloadOptionsOpen(false);
        fetchSessionDetails(page, rowsPerPage, selectedDate);
      });
  };

  // Handler for downloading Processed CSV URL
  const handleDownloadProcessedCSVUrl = () => {
    // Guard against a second click landing while a download is in flight.
    if (isDownloadingCsv || isDownloadingProcessedCsv) return;

    if (!downloadOptionsRow) {
      showToast("No row selected", "error");
      setDownloadOptionsOpen(false);
      return;
    }

    const Name = sessionStorage.getItem("patientName");
    const DeviceId = sessionStorage.getItem("deviceId");

    // If processedCSVUrl already exists, download it directly
    if (downloadOptionsRow.processedCSVUrl) {
      const formattedName = Name?.replace(/\s+/g, "_") || "patient";
      const uuidShort = (downloadOptionsRow.referenceId || "unknown").split(
        "-",
      )[0];
      const startFormatted = formatTimestamp(
        downloadOptionsRow?.startDate || downloadOptionsRow?.startTime || "",
      );
      const endFormatted = formatTimestamp(
        downloadOptionsRow?.endDate || downloadOptionsRow?.endTime || "",
      );
      const deviceId = DeviceId?.replace(/We-Be\s*/i, "") || "device";
      const baseName = `${formattedName}_${startFormatted}_${endFormatted}_${uuidShort}_${deviceId}`;
      const csvFilename = `${baseName}_processed.csv`;
      const logFilename = `${baseName}_log.txt`;

      setIsDownloadingProcessedCsv(true);

      // Bundle CSV + log into a single zip client-side instead of downloading
      // two files (and instead of the backend persisting a zip in S3).
      let downloadTask;
      if (downloadOptionsRow.logFileUrl) {
        setDownloadProgress(0);
        downloadTask = downloadFilesAsZip(
          [
            { url: downloadOptionsRow.processedCSVUrl, filename: csvFilename },
            { url: downloadOptionsRow.logFileUrl, filename: logFilename },
          ],
          `${baseName}.zip`,
          setDownloadProgress,
        );
      } else {
        downloadTask = downloadBlobUrl(downloadOptionsRow.processedCSVUrl, csvFilename);
      }

      downloadTask
        .then(async () => {
          showToast(
            downloadOptionsRow.logFileUrl
              ? "CSV and log file downloaded as ZIP"
              : "Processed CSV file downloaded successfully",
            "success",
          );
          // Call CsvDownloadHandler to track download count
          try {
            await CsvDownloadHandler(downloadOptionsRow.id);
            // Refresh GetSessionDetail to get updated counts
            const refreshPayload = {
              pageIndex: 0,
              pageSize: 50,
              searchString: "",
              selectedDate: selectedDate
                ? selectedDate.format("YYYY-MM-DD")
                : "",
            };
            const refreshResponse = await GetSessionDetail(rowData, refreshPayload);
            if (refreshResponse.statusCode === 200) {
              setRows(refreshResponse.data);
            }
          } catch (error) {
            console.error("Error tracking CSV download:", error?.message);
          }
        })
        .catch((err) => {
          console.error("Processed CSV download error:", err);
          showToast("Error downloading Processed CSV file", "error");
        })
        .finally(() => {
          setIsDownloadingProcessedCsv(false);
          setDownloadProgress(null);
          setDownloadOptionsOpen(false);
        });
    } else {
      // If processedCSVUrl is null, start the processing job
      setDownloadOptionsOpen(false);

      const formattedName = Name?.replace(/\s+/g, "_") || "patient";
      const uuidShort = (downloadOptionsRow.referenceId || "unknown").split(
        "-",
      )[0];
      const startFormatted = formatTimestamp(
        downloadOptionsRow?.startDate || downloadOptionsRow?.startTime || "",
      );
      const endFormatted = formatTimestamp(
        downloadOptionsRow?.endDate || downloadOptionsRow?.endTime || "",
      );
      const deviceId = DeviceId?.replace(/We-Be\s*/i, "") || "device";

      // Show processing popup
      isPopupClosedRef.current = false;
      setDownloadPopupTitle("Processing CSV file, please wait...");
      setDownloadPopupLoading(true);
      setDownloadPopupOpen(true);

      // Call ProcessRawCSVFile API
      (async () => {
        let processResponse;
        try {
          processResponse = await ProcessRawCSVFile(downloadOptionsRow.id);
        } catch (error) {
          processResponse = error;
        }
        if (isPopupClosedRef.current) return;

        if (processResponse.statusCode === 200) {
          const sessionId = downloadOptionsRow.id;

          // Function to poll GetJobStatus
          const pollJobStatus = async () => {
            let statusResponse;
            try {
              statusResponse = await GetJobStatus(sessionId);
            } catch (error) {
              statusResponse = error;
            }
            if (isPopupClosedRef.current) return;

            if (
              statusResponse.statusCode === 200 &&
              statusResponse.data?.status === "COMPLETED"
            ) {
              // Job completed, now refresh GetSessionDetail to get the processedCSVUrl
              const refreshPayload = {
                pageIndex: 0,
                pageSize: 50,
                searchString: "",
                selectedDate: selectedDate
                  ? selectedDate.format("YYYY-MM-DD")
                  : "",
              };

              try {
                const refreshResponse = await GetSessionDetail(rowData, refreshPayload);
                if (isPopupClosedRef.current) return;

                if (refreshResponse.statusCode === 200) {
                  // Find the updated row with processedCSVUrl
                  const updatedRow = refreshResponse.data.find(
                    (r) => r.id === sessionId,
                  );

                  if (
                    updatedRow &&
                    updatedRow.processedCSVUrl &&
                    updatedRow.logFileUrl
                  ) {
                    // Bundle processed CSV + log file into a single zip —
                    // surface the zipping step (with live progress) so it's
                    // clear a ZIP is being built rather than the popup just
                    // hanging.
                    setDownloadProgress(0);
                    setDownloadPopupTitle("Zipping CSV and log file...");
                    try {
                      await downloadFilesAsZip(
                        [
                          {
                            url: updatedRow.processedCSVUrl,
                            filename: `${formattedName}_${startFormatted}_${endFormatted}_${uuidShort}_${deviceId}_processed.csv`,
                          },
                          {
                            url: updatedRow.logFileUrl,
                            filename: `${formattedName}_${startFormatted}_${endFormatted}_${uuidShort}_${deviceId}_log.txt`,
                          },
                        ],
                        `${formattedName}_${startFormatted}_${endFormatted}_${uuidShort}_${deviceId}.zip`,
                        (pct) => {
                          setDownloadProgress(pct);
                          setDownloadPopupTitle(`Zipping CSV and log file... ${pct}%`);
                        },
                      );
                      setDownloadPopupOpen(false);
                      showToast("CSV and log file downloaded as ZIP", "success");
                      handleRefresh();
                    } catch (err) {
                      console.error("Zip download error:", err);
                      setDownloadPopupTitle("Error: Failed to create ZIP file.");
                      setDownloadPopupLoading(false);
                    } finally {
                      setDownloadProgress(null);
                    }
                  } else {
                    setDownloadPopupTitle("Error: Files not ready.");
                    setDownloadPopupLoading(false);
                  }
                }
              } catch (error) {
                // matches original: no explicit failure handling for this refresh call
              }
            } else if (statusResponse.statusCode !== 200) {
              setDownloadPopupTitle("Error: Failed to check status.");
              setDownloadPopupLoading(false);
            }
          };

          // Wait 10 seconds before first poll, then poll every 10 seconds
          pollTimeoutRef.current = setTimeout(() => {
            pollJobStatus();
            pollIntervalRef.current = setInterval(async () => {
              let statusResponse;
              try {
                statusResponse = await GetJobStatus(sessionId);
              } catch (error) {
                statusResponse = error;
              }
              if (isPopupClosedRef.current) return;

              if (
                statusResponse.statusCode === 200 &&
                statusResponse.data?.status === "COMPLETED"
              ) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;

                // Refresh GetSessionDetail to get the processedCSVUrl
                const refreshPayload = {
                  pageIndex: 0,
                  pageSize: 50,
                  searchString: "",
                  selectedDate: selectedDate
                    ? selectedDate.format("YYYY-MM-DD")
                    : "",
                };

                try {
                  const refreshResponse = await GetSessionDetail(rowData, refreshPayload);
                  if (isPopupClosedRef.current) return;

                  if (refreshResponse.statusCode === 200) {
                    const updatedRow = refreshResponse.data.find(
                      (r) => r.id === sessionId,
                    );

                    if (
                      updatedRow &&
                      updatedRow.processedCSVUrl &&
                      updatedRow.logFileUrl
                    ) {
                      // Bundle processed CSV + log file into a single zip —
                      // surface the zipping step (with live progress) so
                      // it's clear a ZIP is being built rather than the
                      // popup just hanging.
                      setDownloadProgress(0);
                      setDownloadPopupTitle("Zipping CSV and log file...");
                      try {
                        await downloadFilesAsZip(
                          [
                            {
                              url: updatedRow.processedCSVUrl,
                              filename: `${formattedName}_${startFormatted}_${endFormatted}_${uuidShort}_${deviceId}_processed.csv`,
                            },
                            {
                              url: updatedRow.logFileUrl,
                              filename: `${formattedName}_${startFormatted}_${endFormatted}_${uuidShort}_${deviceId}_log.txt`,
                            },
                          ],
                          `${formattedName}_${startFormatted}_${endFormatted}_${uuidShort}_${deviceId}.zip`,
                          (pct) => {
                            setDownloadProgress(pct);
                            setDownloadPopupTitle(`Zipping CSV and log file... ${pct}%`);
                          },
                        );
                      } catch (err) {
                        console.error("Zip download error:", err);
                        setDownloadPopupTitle("Error: Failed to create ZIP file.");
                        setDownloadPopupLoading(false);
                        return;
                      } finally {
                        setDownloadProgress(null);
                      }

                      // Call CsvDownloadHandler to track download count
                      try {
                        await CsvDownloadHandler(sessionId);
                        // Refresh GetSessionDetail to get updated counts
                        const trackingRefreshPayload = {
                          pageIndex: 0,
                          pageSize: 50,
                          searchString: "",
                          selectedDate: selectedDate
                            ? selectedDate.format("YYYY-MM-DD")
                            : "",
                        };
                        const trackingResponse = await GetSessionDetail(rowData, trackingRefreshPayload);
                        if (trackingResponse.statusCode === 200) {
                          setRows(trackingResponse.data);
                        }
                      } catch (error) {
                        console.error("Error tracking CSV download:", error?.message);
                      }

                      setDownloadPopupOpen(false);
                      showToast("CSV and log file downloaded as ZIP", "success");
                      handleRefresh();
                    }
                  }
                } catch (error) {
                  // matches original: no explicit failure handling for this refresh call
                }
              }
            }, 10000);
          }, 10000);
        } else {
          if (!isPopupClosedRef.current) {
            setDownloadPopupTitle("Error: Failed to start processing.");
            setDownloadPopupLoading(false);
          }
        }
      })();
    }
  };

  const handleCloseProcessingPopup = () => {
    // Mark that popup was closed by user
    isPopupClosedRef.current = true;
    // Clear any pending API calls
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setDownloadPopupOpen(false);
  };

  return {
    page,
    rowsPerPage,
    rows,
    totalRows,
    isLoading,
    fetchError,
    calendarOpen,
    referenceModal,
    referenceData,
    downloadOptionsOpen,
    downloadOptionsRow,
    isDownloadingCsv,
    isDownloadingProcessedCsv,
    downloadProgress,
    downloadPopupOpen,
    downloadPopupTitle,
    downloadPopupLoading,
    selectedDate,
    highlightedDates,
    setCalendarOpen,
    setReferenceModal,
    setDownloadOptionsOpen,
    handleDateChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleDelete,
    handleView,
    handleTable,
    handleMainTableView,
    handleDownload,
    handleRefresh,
    handleMainTableDownload,
    handleDownloadCSVUrl,
    handleDownloadProcessedCSVUrl,
    handleCloseProcessingPopup,
  };
}
